// components/AuthComponents/login-form.tsx
"use client";

import { useState, useTransition, useEffect, useRef } from "react";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useRouter } from "next/navigation";
import StandaloneOTPInput from "@/components/AuthComponents/AuthUI/otp-input";
import { useActionState } from "react";
import {
  sendOtpAction,
  signInAction,
  verifyOtpAction,
} from "@/app/actions/login";
import { appendSecurityLogFormData } from "@/lib/security-log-client";
import { useTranslations } from "next-intl";

type AuthActionResponse = {
  error?: string;
  success?: boolean | string;
  data?: any;
};

type LoginStep = "email" | "verify-email" | "password";

interface LoginFormProps {
  callbackUrl: string;
}

export default function LoginForm({ callbackUrl }: LoginFormProps) {
  const t = useTranslations("auth");
  const tCommon = useTranslations("common");
  const router = useRouter();
  const [step, setStep] = useState<LoginStep>("email");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [infoMessage, setInfoMessage] = useState("");
  const [isResending, setIsResending] = useState(false);
  const [isPending, startTransition] = useTransition();
  const verifyOtpSubmittedRef = useRef(false);

  // 1) Send OTP
  const [sendOtpState, sendOtpFormAction] = useActionState<
    AuthActionResponse,
    FormData
  >(
    async (_prev, formData) =>
      (await sendOtpAction(formData)) ?? { error: "Unknown error" },
    { error: "" }
  );

  // 2) Verify OTP
  const [verifyOtpState, verifyOtpFormAction] = useActionState<
    AuthActionResponse,
    FormData
  >(
    async (_prev, formData) =>
      (await verifyOtpAction(formData)) ?? { error: "Unknown error" },
    { error: "" }
  );

  // 3) Sign in with password
  const [signInState, signInFormAction] = useActionState<
    AuthActionResponse,
    FormData
  >(
    async (_prev, formData) =>
      (await signInAction(formData)) ?? { error: "Unknown error" },
    { error: "" }
  );

  // Navigate on success / display errors
  useEffect(() => {
    if (!sendOtpState.success && !sendOtpState.error) return;

      if (isResending) {
      if (sendOtpState.error) {
        setError(sendOtpState.error);
        setIsResending(false);
      } else if (sendOtpState.success) {
        setInfoMessage(t("newCodeSent"));
        setIsResending(false);
      }
    } else {
      // Si c'est l'envoi initial
      if (sendOtpState.error) {
        setError(sendOtpState.error);
      } else if (sendOtpState.success) {
        setStep("verify-email");
      }
    }
  }, [sendOtpState, isResending]);

  useEffect(() => {
    if (verifyOtpState.error) {
      setError(verifyOtpState.error);
      verifyOtpSubmittedRef.current = false;
    } else if (verifyOtpState.success) {
      router.push(callbackUrl);
    }
  }, [verifyOtpState, router, callbackUrl]);

  useEffect(() => {
    if (signInState.error) setError(signInState.error);
    else if (signInState.success) router.push(callbackUrl);
  }, [signInState, router, callbackUrl]);

  const handleBack = () => {
    setError("");
    if (step === "email") router.back();
    else setStep("email");
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError(t("emailRequired"));
      return;
    }
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    startTransition(() => sendOtpFormAction(fd));
  };

  const handleVerifyOtp = (code: string) => {
    if (code.length !== 6) {
      setError(t("enterSixDigitCode"));
      return;
    }
    if (verifyOtpSubmittedRef.current || isPending) return;
    verifyOtpSubmittedRef.current = true;
    setError("");
    const fd = new FormData();
    fd.append("email", email);
    fd.append("otp", code);
    appendSecurityLogFormData(fd);
    startTransition(() => verifyOtpFormAction(fd));
  };

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (!email) {
      setError(t("emailRequired"));
      return;
    }
    if (!password) {
      setError(t("passwordRequired"));
      return;
    }
    const fd = new FormData(e.currentTarget as HTMLFormElement);
    appendSecurityLogFormData(fd);
    startTransition(() => signInFormAction(fd));
  };

  const inputClass =
    "w-full min-h-[48px] px-4 py-3.5 rounded-full border border-border bg-background text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 transition-colors text-base";

  return (
    <div className="w-full relative max-w-[26rem] mx-auto sm:max-w-md sm:mx-auto">
      <button
        onClick={handleBack}
        className="absolute -top-1 -left-1 min-w-[44px] min-h-[44px] flex items-center justify-center p-2.5 hover:bg-muted/80 rounded-full text-muted-foreground hover:text-foreground transition-colors touch-manipulation"
        aria-label="Go back"
      >
        <ArrowLeft className="h-5 w-5" />
      </button>

      <div className="text-center mb-6 sm:mb-8 md:mb-10 pt-2">
        <h1 className="text-xl sm:text-2xl md:text-3xl font-bold tracking-tight text-foreground mb-2 leading-tight">
          {step === "email"
            ? t("welcomeBack")
            : step === "verify-email"
              ? t("verifyEmail")
              : t("logIn")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {step === "email"
            ? t("emailOtpSubtitle")
            : step === "verify-email"
              ? t("codeSentTo", { email })
              : t("orSignInWithPassword")}
        </p>
      </div>

      {error && (
        <div
          className={
            error === "This email is not registered. Please create an account first."
              ? "mb-4 p-3.5 rounded-xl bg-amber-50 dark:bg-amber-950/20 text-amber-800 dark:text-amber-200 text-sm border border-amber-200 dark:border-amber-800"
              : "mb-4 p-3.5 rounded-xl bg-destructive/10 text-destructive text-sm border border-destructive/20"
          }
        >
          {error}
        </div>
      )}

      {infoMessage && (
        <div className="mb-4 p-3.5 rounded-xl bg-primary/10 text-foreground text-sm border border-primary/20">
          {infoMessage}
        </div>
      )}

      {step === "email" && (
        <form onSubmit={handleEmailSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
          <input type="hidden" name="callbackUrl" value={callbackUrl} />
          <input
            type="email"
            name="email"
            placeholder={t("email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            required
          />
          <Button type="submit" className="w-full min-h-[48px] py-6 text-base rounded-full touch-manipulation" size="lg">
            {t("sendVerificationCode")}
          </Button>
          <button
            type="button"
            onClick={() => setStep("password")}
            className="w-full text-center text-sm text-primary hover:underline pt-1"
          >
            {t("orLogInWithPassword")}
          </button>
        </form>
      )}

      {step === "verify-email" && (
        <>
          <StandaloneOTPInput
            length={6}
            onComplete={handleVerifyOtp}
            className="w-full py-4"
          />
          <div className="text-center pt-2">
            <button
              type="button"
              onClick={() => {
                setError("");
                setInfoMessage("");
                setIsResending(true);
                const fd = new FormData();
                fd.append("email", email);
                startTransition(() => sendOtpFormAction(fd));
              }}
              disabled={isResending || isPending}
              className="text-sm text-primary hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isResending ? tCommon("resending") : tCommon("resendCode")}
            </button>
          </div>
        </>
      )}

      {step === "password" && (
        <form onSubmit={handlePasswordSubmit} className="space-y-4 sm:space-y-5 md:space-y-6">
          <input
            type="email"
            name="email"
            placeholder={t("email")}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClass}
            required
          />
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              name="password"
              placeholder={t("password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={`${inputClass} pr-12`}
              required
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              aria-label={showPassword ? "Hide password" : "Show password"}
            >
              {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
            </button>
          </div>
          <div className="text-right">
            <button
              type="button"
              onClick={() => router.push("/forgot-password")}
              className="text-sm text-primary hover:underline"
            >
              {t("forgotPassword")}
            </button>
          </div>
          <Button type="submit" className="w-full min-h-[48px] py-6 text-base rounded-full touch-manipulation" size="lg">
            {t("logIn")}
          </Button>
        </form>
      )}
    </div>
  );
}
